import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import {
  BACKPEDAL_FACTOR,
  CLASSES,
  RUN_SPEED,
  TILE_SIZE,
  UNIT_HALF_HEIGHT,
  abilityDef,
  auraDef,
  type AbilityDef,
  type AbilityId,
  type AuraId,
  type ClassId,
  type Effect,
  type School,
} from '@arena/shared';
import { loadModel } from '../lib/assets';
import { Environment, createRenderer } from '../lib/environment';
import { FX_MODELS, SCHOOL_COLORS, SpellFx } from '../game/Fx';
import {
  CLASS_LOOKS,
  FORM_MODELS,
  UnitView,
  castAnimation,
  type Hands,
  type UnitPose,
} from '../game/UnitView';
import { WeaponTrail, type TrailStyle } from '../game/WeaponTrail';

export type Team = 'self' | 'friendly' | 'enemy';
export type Stance =
  'idle' | 'walk' | 'backpedal' | 'run' | 'jump' | 'fall' | 'casting' | 'dead';

/** Character models by path under /models; all share the soldier's rig and clips. */
export const CHARACTERS = [
  'character-soldier',
  'dungeon/character-human',
  'dungeon/character-orc',
  'forest/character-archer',
  'graveyard/character-keeper',
  'graveyard/character-vampire',
  'graveyard/character-zombie',
  'graveyard/character-skeleton',
  'graveyard/character-ghost',
];

export interface Look {
  classId: ClassId;
  character: string;
  hands: Hands;
  team: Team;
}

/** Velocities that put UnitView's animation state machine in each stance; yaw 0 faces -z. */
const STANCES: Record<Stance, Partial<UnitPose>> = {
  idle: {},
  walk: { vel: new THREE.Vector3(0, 0, -2) },
  backpedal: { vel: new THREE.Vector3(0, 0, RUN_SPEED * BACKPEDAL_FACTOR) },
  run: { vel: new THREE.Vector3(0, 0, -RUN_SPEED) },
  jump: { vel: new THREE.Vector3(0, 5, 0), grounded: false },
  fall: { vel: new THREE.Vector3(0, -5, 0), grounded: false },
  casting: { casting: true },
  dead: { dead: true },
};
const NAMEPLATE_HEIGHT = 2.1;
const CIRCLE_RADIUS = 4;
const SELF = 'preview';
const DUMMY = 'dummy';
const SCHOOLS = Object.keys(SCHOOL_COLORS) as School[];

/** An aura an ability put on the preview or the dummy, ticking like the server's. */
interface TimedAura {
  auraId: AuraId;
  bearer: string;
  left: number;
  nextTick: number;
}

/**
 * Three.js side of the character viewer: one UnitView posed by the panel's settings,
 * and a target dummy its abilities play their spell effects on.
 */
export class ViewerEngine {
  stance: Stance = 'idle';
  auras: AuraId[] = [];
  health = 1;
  targeted = false;
  timeScale = 1;
  /** How far in front the dummy stands. */
  distance = 8;
  /** Runs the preview around the origin, whatever the stance's speed. */
  circling = false;
  private lap = 0;
  /** Right hand, then left. */
  private trails: WeaponTrail[] = [];
  private look: Look = {
    classId: 'warrior',
    ...CLASS_LOOKS.warrior,
    team: 'enemy',
  };
  private renderer: THREE.WebGLRenderer;
  private environment: Environment;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(45, 1, 0.1, 300);
  private controls: OrbitControls;
  private view: UnitView | null = null;
  private cast: {
    id: AbilityId;
    ability: AbilityDef;
    elapsed: number;
    duration: number;
    ticks: number;
  } | null = null;
  private soldier: GLTF | null = null;
  private dummy: UnitView | null = null;
  private dummyOn = false;
  private timed: TimedAura[] = [];
  private fx = new SpellFx(this.scene, (id) =>
    id === SELF
      ? (this.view ?? undefined)
      : id === DUMMY
        ? (this.dummy ?? undefined)
        : undefined,
  );
  private lastFrame = performance.now() / 1000;
  private running = true;
  private disposers: (() => void)[] = [];

  constructor(
    private canvas: HTMLCanvasElement,
    private overlay: HTMLElement,
  ) {
    this.renderer = createRenderer(canvas);
    this.camera.position.set(2.5, 2, -4.5);
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.target.set(0, 0.9, 0);
    this.controls.minDistance = 1.5;
    this.controls.maxDistance = 25;
    this.scene.add(
      new THREE.GridHelper(8 * TILE_SIZE, 8, 0xe6c06a, 0x555555).translateY(
        0.01,
      ),
    );
    this.environment = new Environment(this.renderer, this.scene);
  }

  /** Starts rendering; resolves with the characters' clip names. */
  async start(): Promise<string[]> {
    const [soldier, ...fxModels] = await Promise.all(
      ['character-soldier', ...FX_MODELS].map(loadModel),
    );
    if (!this.running) return [];
    this.soldier = soldier;
    this.fx.models = Object.fromEntries(
      FX_MODELS.map((name, i) => [name, fxModels[i].scene]),
    );
    this.syncDummy();
    void this.rebuild();
    const resize = new ResizeObserver(() => this.resize());
    resize.observe(this.canvas.parentElement!);
    this.disposers.push(() => resize.disconnect());
    this.resize();
    requestAnimationFrame(this.frame);
    return soldier.animations.map((clip) => clip.name);
  }

  dispose(): void {
    this.running = false;
    for (const dispose of this.disposers) dispose();
    this.fx.dispose();
    for (const trail of this.trails) trail.dispose();
    this.view?.dispose();
    this.dummy?.dispose();
    this.controls.dispose();
    this.renderer.dispose();
  }

  setLook(look: Look): void {
    this.look = look;
    void this.rebuild();
  }

  /** A weapon trail in a class's color on each of the preview's hands, or none. */
  setTrail(
    trail: { classId: ClassId; style: TrailStyle; seconds: number } | null,
  ): void {
    for (const t of this.trails) t.dispose();
    this.trails = trail
      ? [0, 1].map(
          () =>
            new WeaponTrail(
              CLASSES[trail.classId].color,
              trail.style,
              trail.seconds,
            ),
        )
      : [];
    for (const t of this.trails) this.scene.add(t.mesh);
  }

  setAutoRotate(on: boolean): void {
    this.controls.autoRotate = on;
  }

  play(clip: string): void {
    this.view?.playOnce(clip);
  }

  /** Shows a target dummy for abilities to land on, and frames both units. */
  setDummy(on: boolean): void {
    this.dummyOn = on;
    this.syncDummy();
    if (on) {
      this.camera.position.set(2.5, 4, 6.5);
      this.controls.target.set(0, 0.9, -this.distance / 2);
    } else {
      this.camera.position.set(2.5, 2, -4.5);
      this.controls.target.set(0, 0.9, 0);
    }
  }

  /** Mirrors the game: casts and channels hold the casting pose, then casts (never channels) play their one-shot. */
  useAbility(id: AbilityId): void {
    const ability = abilityDef(id);
    const own = this.timed.find(
      (a) =>
        a.bearer === SELF &&
        ability.effects.some((e) => e.type === 'aura' && e.aura === a.auraId),
    );
    if (ability.cancelable && own)
      return auraDef(own.auraId).stealth
        ? undefined
        : this.removeAura(own, 'cancelled');
    if ((ability.gcd ?? CLASSES[this.look.classId].gcd) > 0) {
      for (const aura of this.timed)
        if (aura.bearer === SELF && auraDef(aura.auraId).form)
          this.removeAura(aura, 'cancelled');
    }
    const duration = ability.channel?.duration ?? ability.castTime ?? 0;
    this.fx.castStop(SELF);
    this.cast =
      duration > 0 ? { id, ability, elapsed: 0, duration, ticks: 0 } : null;
    if (duration > 0)
      this.fx.castStart(SELF, this.targetFor(ability), id, duration);
    else this.succeed(id);
  }

  /** An auto-attack on the dummy. */
  swing(): void {
    if (!this.view || !this.dummy) return;
    const ranged = !!CLASSES[this.look.classId].swing.range;
    this.play(ranged ? 'holding-right-shoot' : 'attack-melee-right');
    this.fx.melee(SELF, DUMMY, ranged);
    this.damaged(DUMMY);
  }

  clearAuras(): void {
    this.timed = [];
  }

  private targetFor(ability: AbilityDef): string | null {
    return ability.target === 'self' || !this.dummy ? null : DUMMY;
  }

  private succeed(id: AbilityId): void {
    const ability = abilityDef(id);
    const targetId = this.targetFor(ability);
    this.play(castAnimation(ability));
    const travel =
      ability.projectileSpeed && targetId
        ? this.distance / ability.projectileSpeed
        : 0;
    this.fx.castSuccess(SELF, targetId, id, travel);
    this.fx.after(travel, () => this.land(ability.effects, targetId));
  }

  /** The parts of an ability's effects that leave something to see afterwards. */
  private land(effects: Effect[], targetId: string | null): void {
    for (const effect of effects) {
      if (effect.type === 'aura')
        this.addAura(effect.aura, effect.self || !targetId ? SELF : targetId);
      if (effect.type === 'aoe' && this.dummy) this.land(effect.effects, DUMMY);
      if (effect.type === 'chain') this.land(effect.effects, targetId);
      if (!targetId) continue;
      if (effect.type === 'damage') this.damaged(targetId);
      if (effect.type === 'interrupt')
        this.fx.interrupted(
          targetId,
          SCHOOLS[Math.floor(Math.random() * SCHOOLS.length)],
        );
      if (effect.type === 'dispel') {
        const aura = this.timed.findLast((a) => a.bearer === targetId);
        if (aura) this.removeAura(aura, 'dispelled');
      }
    }
  }

  /** Breaks the bearer's auras that any damage breaks, like the server. */
  private damaged(bearer: string): void {
    for (const aura of [...this.timed]) {
      if (aura.bearer === bearer && auraDef(aura.auraId).breakOnDamage === 0)
        this.removeAura(aura, 'broken');
    }
  }

  private addAura(auraId: AuraId, bearer: string): void {
    this.timed = this.timed.filter(
      (a) => a.auraId !== auraId || a.bearer !== bearer,
    );
    const def = auraDef(auraId);
    this.timed.push({
      auraId,
      bearer,
      left: def.duration || Infinity,
      nextTick: def.periodic?.interval ?? Infinity,
    });
  }

  private removeAura(
    aura: TimedAura,
    reason: 'expired' | 'dispelled' | 'broken' | 'cancelled',
  ): void {
    this.timed = this.timed.filter((a) => a !== aura);
    this.fx.auraRemoved(aura.bearer, aura.auraId, reason);
  }

  private updateAuras(dt: number): void {
    for (const aura of [...this.timed]) {
      aura.left -= dt;
      aura.nextTick -= dt;
      const periodic = auraDef(aura.auraId).periodic;
      if (periodic && aura.nextTick <= 0) {
        aura.nextTick += periodic.interval;
        this.fx.periodic(aura.bearer, periodic.damage ? periodic.school : null);
        if (periodic.damage) this.damaged(aura.bearer);
      }
      if (aura.left <= 0 && this.timed.includes(aura))
        this.removeAura(aura, 'expired');
    }
  }

  private aurasOn(bearer: string) {
    return this.timed
      .filter((a) => a.bearer === bearer)
      .map((a) => ({
        auraId: a.auraId,
        remaining: Number.isFinite(a.left) ? a.left : 0,
      }));
  }

  private syncDummy(): void {
    if (this.dummyOn && !this.dummy && this.soldier) {
      this.dummy = new UnitView(
        DUMMY,
        'warrior',
        'Target Dummy',
        false,
        false,
        this.soldier,
        {},
        this.overlay,
        { right: null, left: null },
      );
      this.scene.add(this.dummy.root);
    }
    if (!this.dummyOn && this.dummy) {
      this.dummy.dispose();
      this.scene.remove(this.dummy.root);
      this.dummy = null;
      this.timed = this.timed.filter((a) => a.bearer !== DUMMY);
    }
  }

  private async rebuild(): Promise<void> {
    const look = this.look;
    const held = [look.hands.right, look.hands.left].filter(
      (item) => item !== null,
    );
    const [character, ...rest] = await Promise.all(
      [look.character, ...held, ...FORM_MODELS].map(loadModel),
    );
    const items = rest.slice(0, held.length);
    const forms = rest.slice(held.length);
    if (!this.running || look !== this.look) return;
    if (this.view) {
      this.view.dispose();
      this.scene.remove(this.view.root);
    }
    const { classId, team } = look;
    this.view = new UnitView(
      SELF,
      classId,
      CLASSES[classId].name,
      team !== 'enemy',
      team === 'self',
      character,
      Object.fromEntries(held.map((name, i) => [name, items[i].scene])),
      this.overlay,
      look.hands,
      Object.fromEntries(FORM_MODELS.map((name, i) => [name, forms[i]])),
    );
    this.scene.add(this.view.root);
  }

  private frame = (ms: number): void => {
    if (!this.running) return;
    const now = ms / 1000;
    const dt =
      Math.min(0.1, Math.max(0, now - this.lastFrame)) * this.timeScale;
    this.lastFrame = now;
    this.controls.update();
    this.updateAuras(dt);
    if (this.view) this.updateView(this.view, dt);
    if (this.dummy) this.updateDummy(this.dummy, dt);
    this.fx.update(dt);
    this.environment.update(this.camera, this.controls.target);
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(this.frame);
  };

  private updateView(view: UnitView, dt: number): void {
    let progress: number | null = null;
    const cast = this.cast;
    if (cast) {
      cast.elapsed += dt;
      const p = Math.min(1, cast.elapsed / cast.duration);
      progress = cast.ability.channel ? 1 - p : p;
      const channel = cast.ability.channel;
      const targetId = this.targetFor(cast.ability);
      while (
        channel &&
        cast.ticks < channel.ticks &&
        cast.elapsed >= ((cast.ticks + 1) * channel.duration) / channel.ticks
      ) {
        cast.ticks++;
        this.fx.channelTick(cast.id, SELF, targetId ?? SELF);
        this.land(cast.ability.effects, targetId);
      }
      if (p >= 1) {
        this.cast = null;
        progress = null;
        if (!channel) this.succeed(cast.id);
      }
    }
    const own = this.aurasOn(SELF);
    view.setAuras([
      ...this.auras.map((auraId) => ({ auraId, remaining: 0 })),
      ...own,
    ]);
    view.setStealthed(
      [...this.auras, ...own.map((a) => a.auraId)].some(
        (id) => auraDef(id).stealth,
      ),
    );
    view.setHealth(this.health);
    view.setTargeted(this.targeted);
    view.setCast(progress);
    const stance = STANCES[this.stance];
    if (this.circling) this.lap += (dt * RUN_SPEED) / CIRCLE_RADIUS;
    const lap = this.lap;
    view.update(dt, {
      pos: this.circling
        ? new THREE.Vector3(
            Math.cos(lap) * CIRCLE_RADIUS,
            UNIT_HALF_HEIGHT,
            Math.sin(lap) * CIRCLE_RADIUS,
          )
        : new THREE.Vector3(0, UNIT_HALF_HEIGHT, 0),
      yaw: this.circling ? Math.PI - lap : 0,
      vel: this.circling
        ? new THREE.Vector3(-Math.sin(lap), 0, Math.cos(lap)).multiplyScalar(
            RUN_SPEED,
          )
        : (stance.vel ?? new THREE.Vector3()),
      grounded: stance.grounded ?? true,
      dead: stance.dead ?? false,
      casting: (stance.casting ?? false) || this.cast !== null,
    });
    this.trails[0]?.update(dt, view.weaponEdge('right'));
    this.trails[1]?.update(dt, view.weaponEdge('left'));

    this.placeNameplate(view, this.look.team === 'self');
  }

  private updateDummy(dummy: UnitView, dt: number): void {
    dummy.setAuras(this.aurasOn(DUMMY));
    dummy.update(dt, {
      pos: new THREE.Vector3(0, UNIT_HALF_HEIGHT, -this.distance),
      yaw: Math.PI,
      vel: new THREE.Vector3(),
      grounded: true,
      dead: false,
      casting: false,
    });
    this.placeNameplate(dummy, false);
  }

  private placeNameplate(view: UnitView, hidden: boolean): void {
    const projected = view.root.position
      .clone()
      .setY(view.root.position.y + NAMEPLATE_HEIGHT)
      .project(this.camera);
    view.nameplate.style.display = projected.z > 1 || hidden ? 'none' : '';
    view.nameplate.style.transform = `translate(${((projected.x + 1) / 2) * this.canvas.clientWidth}px, ${((1 - projected.y) / 2) * this.canvas.clientHeight}px) translate(-50%, -100%)`;
  }

  private resize(): void {
    const parent = this.canvas.parentElement!;
    this.renderer.setSize(parent.clientWidth, parent.clientHeight, false);
    this.camera.aspect = parent.clientWidth / Math.max(1, parent.clientHeight);
    this.camera.updateProjectionMatrix();
  }
}
